import { requireById } from './dom'
import { showToast } from './admin-ui'

interface Chip {
  label: string
  state: 'ok' | 'warn' | 'hold'
}

interface Guest {
  name: string
  pax: number
  channel: string
  chips: Chip[]
}

interface Crew {
  initials: string
  name: string
  role: string
  /** confirmed = locked in; offered = SMS out, awaiting a reply. */
  status: 'confirmed' | 'offered'
  note?: string
}

interface Departure {
  time: string
  tour: string
  capacity: number
  sold: number
  held?: number
  /** Where the departure is at: departed, boarding, filling, etc. */
  status: string
  statusState: 'done' | 'go' | 'open'
  crew: Crew[]
  guests: Guest[]
  note?: string
}

interface Day {
  id: string
  chip: string
  departures: Departure[]
}

const PAID: Chip = { label: 'Paid ✓', state: 'ok' }
const WAIVER: Chip = { label: 'Waiver ✓', state: 'ok' }

const DAYS: readonly Day[] = [
  {
    id: 'mon',
    chip: 'Today · Mon 10',
    departures: [
      {
        time: '9:00 am',
        tour: 'Morning Whale Cruise',
        capacity: 18,
        sold: 15,
        status: 'Departed 9:04 — 15 aboard',
        statusState: 'done',
        crew: [
          { initials: 'MB', name: 'Mel Baxter', role: 'skipper', status: 'confirmed' },
          { initials: 'JR', name: 'Jonno Reid', role: 'deckhand', status: 'confirmed' },
          { initials: 'PK', name: 'Priya Kumar', role: 'deckhand', status: 'confirmed' },
        ],
        guests: [
          { name: 'Harris family', pax: 4, channel: 'web', chips: [PAID, WAIVER] },
          { name: 'Beth & Ryan Cooper', pax: 2, channel: 'Viator', chips: [PAID, WAIVER] },
          { name: 'Ito party', pax: 3, channel: 'GYG', chips: [PAID, WAIVER] },
          { name: 'Lena Ortiz', pax: 2, channel: 'web', chips: [PAID, WAIVER] },
          { name: 'Walsh group', pax: 4, channel: 'phone', chips: [PAID, WAIVER] },
        ],
      },
      {
        time: '2:00 pm',
        tour: 'Coastal Discovery Tour',
        capacity: 12,
        sold: 9,
        status: 'Go — boarding 1:45 pm',
        statusState: 'go',
        crew: [
          { initials: 'MB', name: 'Mel Baxter', role: 'skipper', status: 'confirmed' },
          { initials: 'JR', name: 'Jonno Reid', role: 'deckhand', status: 'confirmed' },
        ],
        guests: [
          { name: 'Nguyen family', pax: 4, channel: 'web', chips: [PAID, WAIVER] },
          { name: 'Sofia Marino', pax: 2, channel: 'Viator', chips: [PAID, { label: 'Waiver sent 8:00 am', state: 'warn' }] },
          { name: 'Dean Carter', pax: 1, channel: 'phone', chips: [{ label: 'Pay at dock', state: 'warn' }, WAIVER] },
          { name: 'Kylie & Matt Shaw', pax: 2, channel: 'web', chips: [PAID, WAIVER] },
        ],
      },
    ],
  },
  {
    id: 'tue',
    chip: 'Tue 11',
    departures: [
      {
        time: '9:00 am',
        tour: 'Morning Whale Cruise',
        capacity: 18,
        sold: 4,
        held: 2,
        status: '12 seats open',
        statusState: 'open',
        crew: [
          { initials: 'MB', name: 'Mel Baxter', role: 'skipper', status: 'confirmed' },
          { initials: 'JR', name: 'Jonno Reid', role: 'deckhand', status: 'offered', note: 'offered by SMS 7:02 am' },
        ],
        guests: [
          {
            name: 'Sarah Mitchell',
            pax: 4,
            channel: 'phone',
            chips: [PAID, WAIVER, { label: 'Booked by the assistant 8:15 am', state: 'ok' }],
          },
          { name: 'Hold #H-482', pax: 2, channel: 'chat', chips: [{ label: 'Held — expires 8:15 am', state: 'hold' }] },
        ],
        note: 'Tuesdays fill late — today’s post links straight to this departure.',
      },
    ],
  },
  {
    id: 'wed',
    chip: 'Wed 12',
    departures: [
      {
        time: '9:00 am',
        tour: 'Morning Whale Cruise',
        capacity: 18,
        sold: 15,
        status: 'Nearly full — 3 seats left',
        statusState: 'go',
        crew: [
          { initials: 'MB', name: 'Mel Baxter', role: 'skipper', status: 'confirmed' },
          { initials: 'PK', name: 'Priya Kumar', role: 'deckhand', status: 'confirmed' },
          { initials: 'ST', name: 'Sam Torres', role: 'deckhand', status: 'confirmed' },
        ],
        guests: [
          { name: 'Chen family', pax: 4, channel: 'web', chips: [PAID, WAIVER] },
          { name: 'Osman party', pax: 3, channel: 'Viator', chips: [PAID, WAIVER] },
          { name: 'Patel family', pax: 4, channel: 'GYG', chips: [PAID, { label: 'Waiver sent yesterday', state: 'warn' }] },
          { name: 'Grace Lee', pax: 2, channel: 'web', chips: [PAID, WAIVER] },
          { name: 'Jess & Cooper Reed', pax: 2, channel: 'phone', chips: [PAID, WAIVER] },
        ],
      },
    ],
  },
]

function departureCard(dep: Departure): string {
  const soldPct = ((dep.sold / dep.capacity) * 100).toFixed(1)
  const heldPct = dep.held ? ((dep.held / dep.capacity) * 100).toFixed(1) : '0'
  const heldNote = dep.held ? ` · ${dep.held} held` : ''

  const crew = dep.crew
    .map(
      (c) =>
        `<span class="bk-crew ${c.status}"><i>${c.initials}</i>${c.name} · ${c.role}` +
        `<em>${c.status === 'confirmed' ? '✓ confirmed' : `${c.note ?? 'offered'} — awaiting`}</em></span>`,
    )
    .join('')

  const guests = dep.guests
    .map(
      (g) =>
        `<div class="bk-guest"><b>${g.name}</b><span class="bk-pax mono">×${g.pax}</span>` +
        `<span class="bk-ch mono">${g.channel}</span>` +
        `<span class="bk-chips">${g.chips.map((ch) => `<i class="${ch.state}">${ch.label}</i>`).join('')}</span></div>`,
    )
    .join('')

  return `
    <div class="bk-dep">
      <div class="bkd-top">
        <div class="bkd-when"><b>${dep.time}</b><span>${dep.tour}</span></div>
        <div class="bkd-cap">
          <span class="mono">${dep.sold} of ${dep.capacity}${heldNote}</span>
          <span class="bk-meter"><i class="sold" style="width:${soldPct}%"></i><i class="held" style="width:${heldPct}%"></i></span>
        </div>
        <span class="bkd-status ${dep.statusState}">${dep.status}</span>
      </div>
      <div class="bkd-crew"><label>Crew</label>${crew}</div>
      <div class="bkd-guests">${guests}</div>
      ${dep.note ? `<div class="bkd-note mono">${dep.note}</div>` : ''}
    </div>`
}

/**
 * The bookings view: one day at a time — its departures, who's rostered on,
 * and where every guest stands. The day chips (and arrows) imply the real
 * thing pages through the whole season.
 */
export function initBookings(): void {
  const daysBar = requireById('bkdays')
  const body = requireById('bkbody')

  let selected = 0

  function render(): void {
    daysBar.replaceChildren(
      ...[
        arrow('‹', -1),
        ...DAYS.map((d, i) => {
          const chip = document.createElement('button')
          chip.type = 'button'
          chip.className = `avf${i === selected ? ' on' : ''}`
          chip.textContent = d.chip
          chip.addEventListener('click', () => select(i))
          return chip
        }),
        arrow('›', 1),
      ],
    )
    body.innerHTML = DAYS[selected]!.departures.map(departureCard).join('')
  }

  function arrow(glyph: string, dir: number): HTMLButtonElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'avf bk-arrow'
    button.textContent = glyph
    button.setAttribute('aria-label', dir < 0 ? 'Previous day' : 'Next day')
    button.addEventListener('click', () => select(selected + dir))
    return button
  }

  function select(index: number): void {
    if (index < 0 || index >= DAYS.length) {
      showToast('The sample week loads Mon–Wed — the real thing pages through your whole season.')
      return
    }
    selected = index
    render()
  }

  render()
}
